import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import EmptyState from "@/components/feature/EmptyState";
import { works } from "@/mocks/works";
import { news } from "@/mocks/news";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const popularKeywords = ["潮汐之间", "暗巷回声", "影评", "演员动态", "长夜将明"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = (searchParams.get("q") ?? "").trim();
  const [draft, setDraft] = useState(query);

  useEffect(() => {
    setDraft(query);
  }, [query]);

  useDocumentMeta(
    query ? `搜索「${query}」 - 吴梦梦影视官网` : "搜索 - 吴梦梦影视官网",
    query
      ? `吴梦梦影视官网站内搜索结果：与「${query}」相关的作品与资讯。搜索为前端演示功能，未接入外部数据接口。`
      : "搜索吴梦梦的作品与影视资讯，支持按片名、类型、主演、关键词快速定位站内内容（前端演示搜索）。",
    "站内搜索,吴梦梦作品搜索,影视资讯搜索",
  );

  const term = query.toLowerCase();

  const matchedWorks = useMemo(() => {
    if (!term) return [];
    return works.filter((work) =>
      [work.title, work.type, work.director, work.tags.join(""), work.cast.join(""), work.synopsis]
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [term]);

  const matchedNews = useMemo(() => {
    if (!term) return [];
    return news
      .filter((item) =>
        [item.title, item.category, item.author, item.excerpt, item.body.join("")]
          .join(" ")
          .toLowerCase()
          .includes(term),
      )
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [term]);

  const total = matchedWorks.length + matchedNews.length;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = draft.trim();
    if (!next) {
      setSearchParams({});
      return;
    }
    setSearchParams({ q: next });
  };

  const applyKeyword = (keyword: string) => {
    setDraft(keyword);
    setSearchParams({ q: keyword });
  };

  return (
    <div>
      <section className="border-b border-background-200 bg-background-100/60">
        <div className="container-content py-7 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
          <div className="mt-4 md:mt-5 max-w-2xl">
            <span className="text-[10px] md:text-[11px] font-label tracking-[0.22em] text-accent-600 uppercase">
              Search
            </span>
            <h1 className="mt-2 text-2xl md:text-4xl font-bold leading-tight">站内搜索</h1>
            <p className="mt-2.5 text-sm text-foreground-600 leading-relaxed">
              覆盖全部作品与影视资讯。搜索为前端演示功能，仅匹配本站示例数据，不会调用任何外部接口。
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-5 flex flex-col sm:flex-row items-stretch gap-2.5 max-w-2xl">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                type="search"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="输入作品名、类型、主演或关键词"
                aria-label="搜索作品或资讯"
                className="w-full h-11 pl-10 pr-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-500">热门搜索</span>
            {popularKeywords.map((keyword) => (
              <button
                key={keyword}
                type="button"
                onClick={() => applyKeyword(keyword)}
                className="h-7 px-2.5 rounded-full border border-background-300 bg-background-50 text-xs text-foreground-700 hover:border-accent-400 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                {keyword}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="container-content py-8 md:py-12">
        {!query ? (
          <EmptyState
            icon="ri-search-eye-line"
            title="输入关键词开始搜索"
            description="可以试试作品名（如「潮汐之间」）、类型（电影 / 电视剧）、演员名字，或者资讯分类（影评 / 演员动态）。"
          />
        ) : total === 0 ? (
          <EmptyState
            icon="ri-emotion-sad-line"
            title={`没有找到与「${query}」相关的内容`}
            description="换个更短的关键词试试，或者直接浏览全部作品与资讯列表。"
            actionLabel="浏览全部作品"
            onAction={() => navigate("/works")}
          />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-700">
                与 <strong className="text-foreground-950">「{query}」</strong> 相关的结果共{" "}
                <span className="font-label font-bold text-accent-600">{total}</span> 条
              </p>
              <span className="text-[11px] text-foreground-500">演示搜索 · 未接入外部接口</span>
            </div>

            {matchedWorks.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <h2 className="text-lg md:text-xl font-bold flex items-center gap-2">
                    <span className="w-1 h-4 rounded-full bg-accent-500" />
                    影视作品
                    <span className="text-xs font-normal text-foreground-500 font-label">
                      {matchedWorks.length}
                    </span>
                  </h2>
                  <Link
                    to="/works"
                    className="text-xs text-foreground-600 hover:text-accent-600 transition-colors whitespace-nowrap"
                  >
                    全部作品
                  </Link>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                  {matchedWorks.map((work) => (
                    <WorkCard key={work.id} work={work} />
                  ))}
                </div>
              </div>
            )}

            {matchedNews.length > 0 && (
              <div className="mt-10">
                <div className="flex items-center justify-between gap-4 mb-4">
                  <h2 className="text-lg md:text-xl font-bold flex items-center gap-2">
                    <span className="w-1 h-4 rounded-full bg-accent-500" />
                    影视资讯
                    <span className="text-xs font-normal text-foreground-500 font-label">
                      {matchedNews.length}
                    </span>
                  </h2>
                  <Link
                    to="/news"
                    className="text-xs text-foreground-600 hover:text-accent-600 transition-colors whitespace-nowrap"
                  >
                    全部资讯
                  </Link>
                </div>
                <ul className="flex flex-col divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
                  {matchedNews.map((item) => (
                    <li key={item.id} className="py-4">
                      <Link to={`/news/${item.id}`} className="group block">
                        <div className="flex items-center gap-2 text-[11px] text-foreground-500">
                          <span className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-900">
                            {item.category}
                          </span>
                          <span className="font-label">{item.date}</span>
                        </div>
                        <h3 className="mt-1.5 text-sm md:text-[15px] font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors">
                          {item.title}
                        </h3>
                        <p className="mt-1 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2">
                          {item.excerpt}
                        </p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}